import { useToast } from '../../../../ui/toast/useToast';
import { useSalesLocale } from '../../locale';

import React from 'react';
import { BellOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Divider, Drawer, Grid, Space, Spin, Tag, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  addNotificationFromEvent,
  dismissNotification,
  filterNotifications,
  getNotifications,
  getNotificationPreferences,
  getStoredNotificationsForEmployee,
  getUnreadLabel,
  markNotificationRead,
  registerBrowserNotifications,
  shouldSendBrowserNotification,
  triggerBrowserNotification,
  updateNotificationPreferences,
  type NotificationFilters,
  type NotificationPreferences,
  type SalesNotification,
} from '../../services/phase5/notificationCenter';
import type { SalesEventBusPayload } from '../../types';
import { EmptyNotificationsState } from './EmptyNotificationsState';
import { NotificationBadge } from './NotificationBadge';
import { NotificationFilters as NotificationFiltersControl, type NotificationFilterTab } from './NotificationFilters';
import { NotificationItem } from './NotificationItem';
import { NotificationPreferencesModal } from './NotificationPreferencesModal';
import { canonicalSalesLeadTarget } from '../../salesNavigation';
import './notifications.css';

const { Text, Title } = Typography;

const NOTIFICATION_PRIORITY_ORDER: SalesNotification['priority'][] = ['critical', 'high', 'normal', 'low'];
const NOTIFICATION_PRIORITY_LABELS: Record<SalesNotification['priority'], string> = {
  critical: 'Critical now',
  high: 'High priority',
  normal: 'Daily actions',
  low: 'Low priority',
};

const NOTIFICATION_PRIORITY_COLORS: Record<SalesNotification['priority'], string> = {
  critical: 'red',
  high: 'volcano',
  normal: 'blue',
  low: 'default',
};

export interface NotificationCenterProps {
  employeeId: number;
  canView: boolean;
  showEnablePush?: boolean;
  pushBusy?: boolean;
  onEnablePush?: () => Promise<void> | void;
  buttonLabel?: React.ReactNode;
  buttonClassName?: string;
  buttonStyle?: React.CSSProperties;
  iconStyle?: React.CSSProperties;
  buttonIcon?: React.ReactNode;
  overlayZIndex?: number;
  overlayContainer?: () => HTMLElement;
  onNavigate?: () => void;
  onOpenChange?: (open: boolean) => void;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  employeeId,
  canView,
  showEnablePush,
  pushBusy,
  onEnablePush,
  buttonLabel,
  buttonClassName,
  buttonStyle,
  iconStyle,
  buttonIcon,
  overlayZIndex,
  overlayContainer,
  onNavigate,
  onOpenChange,
}) => {
  const { push: pushToast } = useToast();
  const { t: salesText, format: salesFormat, language } = useSalesLocale();
  const screens = Grid.useBreakpoint();

  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [prefsOpen, setPrefsOpen] = React.useState(false);
  React.useEffect(() => {
    onOpenChange?.(open || prefsOpen);
    return () => onOpenChange?.(false);
  }, [open, prefsOpen, onOpenChange]);
  const [filterTab, setFilterTab] = React.useState<NotificationFilterTab>('all');

  const notificationsQuery = useQuery({
    queryKey: ['sales-phase5-notifications', employeeId],
    queryFn: () => getNotifications(employeeId),
    enabled: canView && employeeId > 0 && open,
    initialData: () => getStoredNotificationsForEmployee(employeeId),
    staleTime: 15_000,
  });

  const prefsQuery = useQuery({
    queryKey: ['sales-phase5-notification-prefs'],
    queryFn: getNotificationPreferences,
    enabled: canView,
    staleTime: 60_000,
  });

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(notificationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sales-phase5-notifications', employeeId] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: ({ notificationId, isCritical }: { notificationId: string; isCritical: boolean }) =>
      dismissNotification(notificationId, { isCritical }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sales-phase5-notifications', employeeId] });
    },
  });

  const updatePrefsMutation = useMutation({
    mutationFn: (prefs: NotificationPreferences) => updateNotificationPreferences(prefs),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sales-phase5-notification-prefs'] });
    },
  });

  const preferences = prefsQuery.data || DEFAULT_NOTIFICATION_PREFERENCES;
  const allItems = React.useMemo(() => notificationsQuery.data || [], [notificationsQuery.data]);

  const filters: NotificationFilters = React.useMemo(
    () => ({ tab: filterTab as NotificationFilters['tab'] }),
    [filterTab],
  );

  const visibleItems = React.useMemo(() => filterNotifications(allItems, filters), [allItems, filters]);
  const visiblePriorityGroups = React.useMemo(
    () => NOTIFICATION_PRIORITY_ORDER
      .map((priority) => ({
        priority,
        items: visibleItems.filter((item) => item.priority === priority),
      }))
      .filter((group) => group.items.length > 0),
    [visibleItems],
  );
  const unreadLabel = React.useMemo(() => getUnreadLabel(allItems), [allItems]);

  React.useEffect(() => {
    if (!canView || employeeId <= 0) {
      return;
    }

    const onSalesEvent = (rawEvent: Event) => {
      const custom = rawEvent as CustomEvent<SalesEventBusPayload>;
      if (!custom.detail) {
        return;
      }
      const item = addNotificationFromEvent(custom.detail, employeeId);
      if (!item) {
        return;
      }
      queryClient.setQueryData<SalesNotification[]>(
        ['sales-phase5-notifications', employeeId],
        (previous) => {
          const base = Array.isArray(previous) && previous.length
            ? previous
            : getStoredNotificationsForEmployee(employeeId);
          if (base.some((row) => row.id === item.id)) {
            return base;
          }
          return [item, ...base].slice(0, 200);
        },
      );
      queryClient.invalidateQueries({ queryKey: ['sales-phase5-notifications', employeeId] });

      if (shouldSendBrowserNotification(item, preferences)) {
        triggerBrowserNotification(item);
      }
    };

    window.addEventListener('sales-event', onSalesEvent as EventListener);
    return () => window.removeEventListener('sales-event', onSalesEvent as EventListener);
  }, [canView, employeeId, preferences, queryClient]);

  if (!canView) {
    return null;
  }

  const onOpenNotification = (item: SalesNotification) => {
    onNavigate?.();
    if (!item.is_read) {
      markReadMutation.mutate(item.id);
    }
    if (item.action_payload && item.action_payload.action === 'open_task') {
      window.dispatchEvent(new CustomEvent('sales-open-tab', { detail: { tab: 'tasks' } }));
      setOpen(false);
      return;
    }
    if (item.action_payload && item.action_payload.action === 'open_workmail') {
      window.dispatchEvent(new CustomEvent('sales-open-tab', { detail: { tab: 'history' } }));
      setOpen(false);
      return;
    }

    if (item.related_lead_id) {
      const target = canonicalSalesLeadTarget(item.related_lead_id, location);
      if (target) void navigate(target, { replace: false, state: location.state });
      setOpen(false);
      return;
    }
    if (item.action_url) {
      void navigate(item.action_url, { replace: false, state: location.state });
      setOpen(false);
    }
  };

  return (
    <>
      <NotificationBadge unreadLabel={unreadLabel}>
        <Button
          type="text"
          icon={buttonIcon ?? <BellOutlined style={{ fontSize: 18, ...iconStyle }} />}
          onClick={() => setOpen(true)}
          aria-label={salesFormat('Notifications, {0} unread', [unreadLabel])}
          className={buttonClassName}
          style={buttonStyle}
        >
          {buttonLabel}
        </Button>
      </NotificationBadge>

      <Drawer
        zIndex={overlayZIndex}
        getContainer={overlayContainer}
        title={
          <Space orientation="vertical" size={0}>
            <Title level={5} style={{ margin: 0 }}>{salesText("Notification Center")}</Title>
            <Text type="secondary" data-sales-explanation>{salesText("Critical updates first, then daily actions.")}</Text>
          </Space>
        }
        placement={language === 'ar' ? 'left' : 'right'}
        rootClassName="sales-notification-center"
        width={screens.md ? 420 : '100%'}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button size="small" icon={<SettingOutlined />} onClick={() => setPrefsOpen(true)}>{salesText("Preferences")}</Button>
            <Space>
              {showEnablePush && onEnablePush ? (
                <Button size="small" loading={pushBusy} onClick={() => void onEnablePush()}>{salesText("Enable Push")}</Button>
              ) : null}
            </Space>
          </Space>

          <NotificationFiltersControl value={filterTab} onChange={setFilterTab} />
          <Divider style={{ margin: '8px 0' }} />

          {notificationsQuery.isLoading ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : null}

          {!notificationsQuery.isLoading && visibleItems.length === 0 ? <EmptyNotificationsState /> : null}

          <Space orientation="vertical" size={14} style={{ width: '100%' }}>
            {visiblePriorityGroups.map((group) => (
              <div key={group.priority} style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <Text strong>{salesText(NOTIFICATION_PRIORITY_LABELS[group.priority])}</Text>
                  <Tag color={NOTIFICATION_PRIORITY_COLORS[group.priority]} style={{ margin: 0 }}>
                    {group.items.length}
                  </Tag>
                </div>
                <Space orientation="vertical" size={10} style={{ width: '100%' }}>
                  {group.items.map((item) => (
                    <NotificationItem
                      key={item.id}
                      item={item}
                      canDismiss={item.priority !== 'critical'}
                      onOpen={onOpenNotification}
                      onMarkRead={(next) => markReadMutation.mutate(next.id)}
                      onDismiss={(next) => dismissMutation.mutate({ notificationId: next.id, isCritical: next.priority === 'critical' })}
                    />
                  ))}
                </Space>
              </div>
            ))}
          </Space>
        </Space>
      </Drawer>

      <NotificationPreferencesModal
        zIndex={overlayZIndex === undefined ? undefined : overlayZIndex + 100}
        getContainer={overlayContainer}
        open={prefsOpen}
        value={preferences}
        saving={updatePrefsMutation.isPending}
        onCancel={() => setPrefsOpen(false)}
        onSave={(next) => {
          void (async () => {
            const enableBrowserNotifications = next.enableBrowserNotifications && !preferences.enableBrowserNotifications
              ? await registerBrowserNotifications()
              : next.enableBrowserNotifications;
            if (next.enableBrowserNotifications && !enableBrowserNotifications) {
              pushToast(salesText('Browser notifications were not enabled. Check browser permissions.'), 'warning', 3000);
            }
            updatePrefsMutation.mutate({ ...next, enableBrowserNotifications }, {
            onSuccess: () => {
              setPrefsOpen(false);
            },
            });
          })();
        }}
      />
    </>
  );
};
